import React from 'react'
import {
  Box,
  TextField,
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteChangeReason,
  AutocompleteChangeDetails
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Product } from 'hooks/useProduct'

interface SearchBarProps {
  searchKeyword: string
  setSearchKeyword: (value: string) => void
  suggestions: Product[]
  addProductToResults: (product: Product) => void
  handleSearchChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => void
  handleSuggestionSelect: (
    event: React.SyntheticEvent<Element, Event>,
    product: Product | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<Product>
  ) => void
}

const QuickOrderSearchBar: React.FC<SearchBarProps> = ({
  searchKeyword,
  setSearchKeyword,
  suggestions,
  addProductToResults,
  handleSearchChange,
  handleSuggestionSelect
}) => {
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        position: 'relative',
        flexGrow: 1,
        margin: '0 auto',
        mb: '30px'
      }}
    >
      <Box
        sx={{
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #D1D1D1',
          padding: '8px 16px',
          height: '48px',
          backgroundColor: '#fff',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: '0 2px 8px rgba(161, 30, 32, 0.1)'
          },
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: '0 2px 12px rgba(161, 30, 32, 0.15)'
          }
        }}
      >
        <Autocomplete
          freeSolo={false}
          options={suggestions}
          value={null}
          getOptionLabel={(option: Product) => option.sku}
          onInputChange={handleSearchChange}
          onChange={handleSuggestionSelect}
          inputValue={searchKeyword}
          disablePortal
          renderOption={(props, option) => (
            <Box
              component='li'
              {...props}
              sx={{
                padding: '12px 16px !important',
                borderBottom: '1px solid #f0f0f0',
                transition: 'all 0.15s ease',
                '&:last-child': {
                  borderBottom: 'none'
                },
                '&:hover': {
                  backgroundColor: 'rgba(161, 30, 32, 0.08) !important'
                },
                '&.Mui-focused': {
                  backgroundColor: 'rgba(161, 30, 32, 0.08) !important'
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box
                  component='span'
                  sx={{ fontWeight: 600, color: 'primary.main' }}
                >
                  {option.sku}
                </Box>
                <Box
                  component='span'
                  sx={{ fontSize: '12px', color: 'text.secondary' }}
                >
                  {option.color}
                </Box>
              </Box>
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              placeholder={t('quickOrder.enterSku')}
              variant='standard'
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                startAdornment: (
                  <Box
                    component='img'
                    src='/magnifying_glass_icon.svg'
                    alt='search icon'
                    sx={{
                      width: '17.19px',
                      height: '17.19px',
                      cursor: 'pointer',
                      marginRight: '10px',
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.1)'
                      }
                    }}
                    onClick={() => {
                      if (suggestions.length > 0) {
                        addProductToResults(suggestions[0])
                        setSearchKeyword('')
                      }
                    }}
                  />
                )
              }}
              sx={{
                width: '100%',
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '16.94px',
                backgroundColor: 'transparent'
              }}
            />
          )}
          ListboxProps={{
            sx: {
              padding: 0,
              maxHeight: '300px'
            }
          }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
                border: '1px solid #e0e0e0',
                marginTop: '8px',
                overflow: 'hidden',
                position: 'absolute',
                left: 0,
                right: 0,
                width: '100%'
              }
            },
            popper: {
              sx: {
                position: 'absolute !important',
                left: '0 !important',
                right: '0 !important',
                width: '100% !important'
              }
            }
          }}
          sx={{
            width: '100%',
            '& .MuiAutocomplete-popper': {
              position: 'absolute !important',
              left: '-16px !important',
              width: 'calc(100% + 32px) !important'
            }
          }}
        />
      </Box>
    </Box>
  )
}

export default QuickOrderSearchBar
